import React, { useState, useEffect } from 'react';
import { Icon } from '@gs/fe-storybook-gasco';
import { RutAndDocumentInputProps } from '../../../../../interfaces/interfaceSteps';
import cvImage from '../../../../../assets/images/cv-image.svg';
import './RutAndDocumentInput.css';

const RutAndDocumentInput: React.FC<RutAndDocumentInputProps> = ({
  value,
  onChange,
  placeholders,
  validateRut,
  validateDocumentNumber,
  rutError,
  documentNumberError,
  onValidityChange,
  setIsServiceExecuted,
  setErrorMessage,
}) => {
  const [rut, setRut] = useState<string>(
    Array.isArray(value) ? value[0] || '' : ''
  );
  const [documentNumber, setDocumentNumber] = useState<string>(
    Array.isArray(value) ? value[2] || '' : ''
  );
  const [isRutFocused, setIsRutFocused] = useState<boolean>(false);
  const [isDocFocused, setIsDocFocused] = useState<boolean>(false);
  const [imageVisible, setImageVisible] = useState<boolean>(false);
  const [isRutAndDocumentValid, setIsRutAndDocumentValid] =
    useState<boolean>(false);

  useEffect(() => {
    const isRutValid = validateRut(rut);
    const isDocValid = validateDocumentNumber(documentNumber);
    const isRutFilled = rut !== '';
    const isDocFilled = documentNumber !== '';

    const isBothFieldsValid =
      isRutValid && isDocValid && isRutFilled && isDocFilled;
    setIsRutAndDocumentValid(isBothFieldsValid);

    if (onValidityChange) {
      onValidityChange(isBothFieldsValid);
    }
  }, [
    rut,
    documentNumber,
    validateRut,
    validateDocumentNumber,
    onValidityChange,
  ]);

  const formatRut = (rutValue: string) => {
    let cleanRut = rutValue.replace(/[^0-9kK]/g, '');
    const numbers = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1).toUpperCase();

    const formattedNumbers = numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return numbers ? `${formattedNumbers}-${dv}` : '';
  };

  const handleRutChange = (newValue: string, input: HTMLInputElement) => {
    if (setIsServiceExecuted) {
      setIsServiceExecuted(false);
    }
    if (setErrorMessage) {
      setErrorMessage('');
    }
    const cursorPosition = input.selectionStart || 0;
    const cleanValue = newValue.replace(/[^0-9kK]/g, '').toUpperCase();
    if (!cleanValue) {
      setRut('');
      return;
    }
    const numbers = cleanValue.slice(0, cleanValue.length - 1);
    const dv = cleanValue.slice(-1);
    const formattedNumbers = numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const formattedRut = numbers ? `${formattedNumbers}-${dv}` : dv;
    setRut(formattedRut);

    setTimeout(() => {
      const newCursorPosition =
        cursorPosition + (formattedRut.length - newValue.length);
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleRutBlur = () => {
    const formattedRut = formatRut(rut);
    setRut(formattedRut);
    handleBlur();
  };

  const handleDocumentNumberChange = (
    newValue: string,
    input: HTMLInputElement
  ) => {
    if (setIsServiceExecuted) {
      setIsServiceExecuted(false);
    }
    if (setErrorMessage) {
      setErrorMessage('');
    }
    const cursorPosition = input.selectionStart || 0;

    const cleanValue = newValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    let formattedDocumentNumber = cleanValue;

    if (/^\d+$/.test(cleanValue)) {
      formattedDocumentNumber = cleanValue.replace(
        /(\d{1,3})(\d{3})?(\d{3})?/,
        (_, p1, p2, p3) => [p1, p2, p3].filter(Boolean).join('.')
      );
    }

    setDocumentNumber(formattedDocumentNumber);

    setTimeout(() => {
      const newCursorPosition =
        cursorPosition + (formattedDocumentNumber.length - newValue.length);
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleBlur = () => {
    if (rut !== value[0] || documentNumber !== value[2]) {
      return;
    }

    const formattedRut = rut.replace(/\./g, '').split('-');
    const formattedDoc = documentNumber.replace(/\./g, '');

    if (formattedRut.length === 2 && formattedDoc) {
      onChange([formattedRut[0], formattedRut[1], formattedDoc]);
    } else {
      console.error('Faltan valores para el RUT o Documento.');
    }
  };

  const handleFocusRut = () => {
    setIsRutFocused(true);
  };

  const handleFocusDoc = () => {
    setIsDocFocused(true);
  };

  const toggleImage = () => {
    setImageVisible(!imageVisible);
  };

  return (
    <>
      <div className="rutContent" id="page-rut" data-page-id="rut">
        <label
          className={`floating-rut ${
            isRutFocused || rut || rutError ? "filled" : ""
          } ${rutError ? "error" : ""}`}
        >
          Rut
        </label>
        <input
          id="rutInput"
          className={`rut ${rutError ? "error" : ""}`}
          type="text"
          placeholder={placeholders.rut}
          maxLength={12}
          value={rut}
          onFocus={handleFocusRut}
          onChange={(e) => handleRutChange(e.target.value, e.target)}
          onBlur={handleRutBlur}
        />
        {rutError && <div className="error">{rutError}</div>}
        <br />
        <label
          className={`floating-rut ${
            isDocFocused || documentNumber || documentNumberError
              ? "filled"
              : ""
          } ${documentNumberError ? "error" : ""}`}
        >
          Número de documento
        </label>
        <input
          id="documentNumberInput"
          className={`document ${documentNumberError ? "error" : ""}`}
          type="text"
          placeholder={placeholders.documentNumber}
          maxLength={11}
          value={documentNumber}
          onFocus={handleFocusDoc}
          onChange={(e) => handleDocumentNumberChange(e.target.value, e.target)}
          onBlur={handleBlur}
        />
        <div className="icon">
          <span className="document-icon" onClick={toggleImage}>
            <Icon name="question-mark-circle" />
          </span>
        </div>

        {documentNumberError && (
          <div className="error">{documentNumberError}</div>
        )}
        {imageVisible && (
          <div
            className={`image-modal ${
              documentNumberError ? "image-modal-error" : ""
            }`}
          >
            <img src={cvImage} alt="Imagen" />
          </div>
        )}
      </div>
    </>
  );
};

export default RutAndDocumentInput;
